import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Leaf, Sun, Award, MapPin } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const milestones = [{
  year: '1985',
  event: 'Supradhyan Enlightenment Center founded by Satguru Dev'
}, {
  year: '1992',
  event: 'Expanded to 50-acre campus with meditation halls and gardens'
}, {
  year: '2000',
  event: 'Launched international retreat programs'
}, {
  year: '2010',
  event: 'Established online meditation courses reaching global audience'
}, {
  year: '2015',
  event: 'Opened residential ashram for long-term practitioners'
}, {
  year: '2020',
  event: 'Celebrated 35 years of service with 10,000+ graduates'
}];
const facilities = [{
  icon: Sun,
  title: 'Meditation Halls',
  description: 'Three spacious halls designed for group practice, each accommodating up to 100 practitioners with natural lighting and acoustic excellence'
}, {
  icon: Leaf,
  title: 'Sacred Gardens',
  description: 'Beautifully landscaped gardens with walking paths, lotus ponds, and meditation groves for contemplative practice in nature'
}, {
  icon: Heart,
  title: 'Wellness Center',
  description: 'Holistic health facility offering Ayurvedic treatments, yoga therapy, and energy healing sessions'
}, {
  icon: Users,
  title: 'Residential Ashram',
  description: 'Comfortable accommodations for retreat participants and long-term residents, with shared and private rooms'
}, {
  icon: Award,
  title: 'Library & Study Rooms',
  description: 'Extensive collection of spiritual texts, meditation resources, and quiet spaces for study and reflection'
}, {
  icon: MapPin,
  title: 'Organic Farm',
  description: 'On-site organic farm providing fresh, sattvic food for all meals, cultivated with mindfulness and care'
}];
export function AboutCenterPage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-263">
      <Header data-id="element-264" />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] w-full overflow-hidden" data-id="element-265">
        <motion.div initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="absolute inset-0" data-id="element-266">
          <img src="https://images.unsplash.com/photo-1544367563-12123d8965cd?q=80&w=2070&auto=format&fit=crop" alt="Meditation center grounds" className="w-full h-full object-cover" data-id="element-267" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#FFFBF5]" data-id="element-268" />
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 pb-16" data-id="element-269">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-270">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="max-w-4xl" data-id="element-271">
              <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight drop-shadow-2xl" data-id="element-272">
                About The Center
              </h1>
              <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow-lg" data-id="element-273">
                A sanctuary for spiritual growth since 1985
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white" data-id="element-274">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-275">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-276">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-277">
              Our Mission & Vision
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16" data-id="element-278">
              <div className="bg-[#FAF7F2] p-8 rounded-2xl" data-id="element-279">
                <h3 className="font-serif text-2xl text-[#8B9D83] mb-4" data-id="element-280">
                  Mission
                </h3>
                <p className="text-[#5D6D7E] leading-relaxed" data-id="element-281">
                  To provide authentic spiritual teachings and practices that
                  empower individuals to discover inner peace, realize their
                  true nature, and live with purpose, compassion, and wisdom in
                  the modern world.
                </p>
              </div>

              <div className="bg-[#FAF7F2] p-8 rounded-2xl" data-id="element-282">
                <h3 className="font-serif text-2xl text-[#8B9D83] mb-4" data-id="element-283">
                  Vision
                </h3>
                <p className="text-[#5D6D7E] leading-relaxed" data-id="element-284">
                  A world where meditation and mindfulness are accessible to
                  all, creating communities of awakened individuals who
                  contribute to global peace, harmony, and collective
                  consciousness evolution.
                </p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none" data-id="element-285">
              <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-286">
                Founded in 1985 by Satguru Dev, Supradhyan Enlightenment Center
                has been a beacon of light for spiritual seekers from all walks
                of life. Nestled in the serene foothills, our 50-acre campus
                provides a sacred space where time slows down and the spirit
                awakens.
              </p>

              <p className="text-lg text-[#5D6D7E] leading-relaxed" data-id="element-287">
                We honor the ancient Vedic traditions while adapting practices
                for contemporary life. Our holistic approach combines
                meditation, yoga, breathwork, and conscious living to support
                complete transformation—body, mind, and soul.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-288">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-289">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-290">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-291">
              Our Journey
            </h2>

            <div className="relative" data-id="element-292">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#8B9D83]/30 hidden md:block" data-id="element-293" />

              <div className="space-y-8" data-id="element-294">
                {milestones.map((milestone, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="relative flex items-start gap-6" data-id="element-295">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#8B9D83] text-white flex items-center justify-center font-serif font-bold z-10" data-id="element-296">
                      {milestone.year.slice(-2)}
                    </div>
                    <div className="flex-grow bg-white p-6 rounded-xl shadow-md" data-id="element-297">
                      <div className="text-sm text-[#D4AF37] font-semibold mb-1" data-id="element-298">
                        {milestone.year}
                      </div>
                      <p className="text-[#2C3E50] font-medium" data-id="element-299">
                        {milestone.event}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 bg-white" data-id="element-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-301">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-302">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-303">
              Our Facilities
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12 max-w-2xl mx-auto" data-id="element-304">
              Everything you need for a transformative spiritual retreat
              experience
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-305">
              {facilities.map((facility, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#FAF7F2] p-6 rounded-xl" data-id="element-306">
                  <div className="w-12 h-12 rounded-full bg-[#8B9D83]/20 flex items-center justify-center mb-4" data-id="element-307">
                    <facility.icon className="w-6 h-6 text-[#8B9D83]" data-id="element-308" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-2" data-id="element-309">
                    {facility.title}
                  </h3>
                  <p className="text-[#5D6D7E]" data-id="element-310">{facility.description}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-311">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-312">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-313">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-314">
              Experience Our Space
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-315">
              {['https://images.unsplash.com/photo-1544367563-12123d8965cd?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1545389336-cf090694435e?q=80&w=2071&auto=format&fit=crop', 'https://images.unsplash.com/photo-1528319725582-ddc096101511?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=2072&auto=format&fit=crop', 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=1964&auto=format&fit=crop'].map((image, index) => <motion.div key={index} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="aspect-square rounded-xl overflow-hidden shadow-md" data-id="element-316">
                  <img src={image} alt={`Center facility ${index + 1}`} className="w-full h-full object-cover" data-id="element-317" />
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#8B9D83] to-[#7A8C72] text-white" data-id="element-318">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-319">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-320">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-321">Visit Us</h2>
            <p className="text-xl mb-10 text-white/90" data-id="element-322">
              Experience the tranquility of our center in person. Schedule a
              visit or join us for an upcoming retreat.
            </p>
            <button className="bg-white text-[#8B9D83] hover:bg-[#FAF7F2] px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl" data-id="element-323">
              Plan Your Visit
            </button>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-324" />
    </div>;
}